import { useState, useRef, useEffect } from 'react';
import { ChevronDown, Loader2, Coins, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import type { WalletToken } from '@/hooks/useTokenBalances';

interface TokenSelectorProps {
  tokens: WalletToken[];
  solBalance: string;
  selectedToken: WalletToken | null;
  onSelect: (token: WalletToken | null) => void;
  isLoading: boolean;
  disabled?: boolean;
}

export const TokenSelector = ({
  tokens,
  solBalance,
  selectedToken,
  onSelect,
  isLoading,
  disabled,
}: TokenSelectorProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [copiedMint, setCopiedMint] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  const hasTokens = tokens.length > 0;
  const canOpen = hasTokens && !disabled;

  const handleToggle = () => {
    if (canOpen) setIsOpen((prev) => !prev);
  };

  const handleSelect = (token: WalletToken | null) => {
    onSelect(token);
    setIsOpen(false);
  };

  const handleCopyMint = (e: React.MouseEvent, mint: string) => {
    e.stopPropagation();
    navigator.clipboard.writeText(mint);
    toast.success('Mint address copied');
    setCopiedMint(mint);
    setTimeout(() => setCopiedMint(null), 1500);
  };

  return (
    <div ref={containerRef} className="relative">
      {/* Collapsed trigger */}
      <button
        type="button"
        onClick={handleToggle}
        disabled={!canOpen}
        className={`flex items-center gap-2 px-2 py-1 rounded-md transition-colors ${
          canOpen
            ? 'hover:bg-muted/50 cursor-pointer'
            : 'cursor-default'
        }`}
      >
        {/* Token icon */}
        {selectedToken ? (
          selectedToken.imageUrl ? (
            <img
              loading="lazy"
              src={selectedToken.imageUrl}
              alt={selectedToken.symbol}
              className="w-6 h-6 rounded-full object-cover"
            />
          ) : (
            <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center">
              <Coins className="w-3.5 h-3.5 text-muted-foreground" />
            </div>
          )
        ) : (
          <div className="w-6 h-6 rounded-full bg-black flex items-center justify-center overflow-hidden flex-shrink-0">
            <img loading="lazy" src="/solana-logo.svg" alt="SOL" className="w-4 h-4" />
          </div>
        )}

        {/* Symbol */}
        <span className="text-sm font-medium">
          {selectedToken ? selectedToken.symbol : 'SOL'}
        </span>

        {/* Chevron (only if there are SPL tokens) */}
        {hasTokens && (
          <ChevronDown
            className={`w-3.5 h-3.5 text-muted-foreground transition-transform ${
              isOpen ? 'rotate-180' : ''
            }`}
          />
        )}

        {/* Loading indicator */}
        {isLoading && (
          <Loader2 className="w-3.5 h-3.5 text-muted-foreground animate-spin" />
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 top-full left-0 mt-1 w-64 max-h-60 overflow-y-auto rounded-lg border border-border bg-secondary/95 backdrop-blur-sm shadow-lg">
          {/* SOL entry - always first */}
          <button
            type="button"
            onClick={() => handleSelect(null)}
            className={`w-full flex items-center gap-3 px-3 min-h-[44px] transition-colors hover:bg-accent/10 active:bg-accent/20 cursor-pointer ${
              selectedToken === null ? 'bg-muted/30' : ''
            }`}
          >
            <div className="w-6 h-6 rounded-full bg-black flex items-center justify-center overflow-hidden flex-shrink-0">
              <img loading="lazy" src="/solana-logo.svg" alt="SOL" className="w-4 h-4" />
            </div>
            <span className="text-sm font-medium">SOL</span>
            <span className="text-sm text-muted-foreground ml-auto">
              {solBalance}
            </span>
          </button>

          {/* SPL tokens */}
          {tokens.map((token) => (
            <div
              key={token.mint}
              role="button"
              tabIndex={0}
              onClick={() => handleSelect(token)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleSelect(token);
                }
              }}
              className={`w-full flex items-start gap-3 px-3 py-2 min-h-[44px] transition-colors hover:bg-accent/10 active:bg-accent/20 cursor-pointer ${
                selectedToken?.mint === token.mint ? 'bg-muted/30' : ''
              }`}
            >
              {token.imageUrl ? (
                <img
                  loading="lazy"
                  src={token.imageUrl}
                  alt={token.symbol}
                  className="w-6 h-6 rounded-full object-cover flex-shrink-0 mt-0.5"
                />
              ) : (
                <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Coins className="w-3.5 h-3.5 text-muted-foreground" />
                </div>
              )}
              <div className="flex flex-col min-w-0 flex-1">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium truncate">{token.symbol}</span>
                  <span className="text-sm text-muted-foreground flex-shrink-0 ml-2">
                    {token.uiAmountString}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-xs text-muted-foreground">
                    {token.mint.slice(0, 4)}...{token.mint.slice(-4)}
                  </span>
                  <button
                    type="button"
                    onClick={(e) => handleCopyMint(e, token.mint)}
                    className="p-0.5 rounded hover:bg-muted/50 transition-colors flex-shrink-0"
                  >
                    {copiedMint === token.mint ? (
                      <Check className="w-3.5 h-3.5 text-green-500" />
                    ) : (
                      <Copy className="w-3.5 h-3.5 text-muted-foreground" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
